//Game view

import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import { RootState } from '../../store';
import StatusBar from './StatusBar';
import { useDailyExercises } from '../../hooks/useDailyExercises';
import DailyExerciseModal from './DailyExerciseModal';
import GameGrid from './GameGrid';
import ActionMenu from './ActionMenu';
import { ASSET_PATHS, GAME_CONFIG } from '../../utils/gameConfig';
import useKeyboard from '../../hooks/useKeyboard';
import { useForest } from '../../hooks/useForest';
import { recordUserActivity, activityManager } from '../../services/activityManager';
import { 
  getAvailableActions, 
  calculateMovementWithTrees,
  getTileType, 
  hasTreeAt,
  getTargetInDirection 
} from '../../services/gameLogic';
import { updateUser, updateTreeGrowthStages, getPlantingErrorMessage } from '../../services/firestore';
import { updateUser as updateUserAction } from '../../store/slices/authSlice';
import { useUserPresence } from '../../hooks/useUserPresence';
import { GameAction } from '../../types';
import { COLOR_PALETTE, ColorKey, DEFAULT_COLOR_KEY } from '../../utils/colors';

const ForestView: React.FC = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { showTreeBubbles, showUserBubbles } = useSelector((state: RootState) => state.ui);
  
  //Use forest hook
  const { currentForest, trees, isLoading, error, handlePlantTree, handleWaterTree } = useForest();

  useEffect(() => {
    if (currentForest) {
      (window as any).__currentForest = currentForest;
    }
  }, [currentForest]);

  useEffect(() => {
  if (currentForest?.id && !isLoading) {
    console.log('User entered forest, updating tree growth stages');
    
    //Update on enter
    updateTreeGrowthStages(currentForest.id)
      .then(() => console.log('Tree growth stages updated'))
      .catch(error => console.error('Failed to update tree growth:', error));
    
    //update based on game type
    let intervalId: NodeJS.Timeout | null = null;
    
    if (currentForest.isQuickGame) {
      //Quick: check every 3 secs
      intervalId = setInterval(() => {
        if (activityManager.isActive()) {
          updateTreeGrowthStages(currentForest.id)
            .catch(error => console.error('Quick game tree update failed:', error));
        }
      }, 3000); //3 seconds
      console.log('Quick game detected - tree updates every 3 seconds');
    } else {
      //Normal (check for tree updates every 5 min)
      intervalId = setInterval(() => {
        if (activityManager.isActive()) {
          updateTreeGrowthStages(currentForest.id)
            .catch(error => console.error('Tree update failed:', error));
        }
      }, 300000); //SETTING: Checks for tree updates every 5 minutes
      console.log('Normal game - tree updates every 5 minutes');
    }
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }
}, [currentForest?.id, currentForest?.isQuickGame, isLoading]);

  const {
    ghostUsers,
    handlePositionChange,
    totalGhostUsers,
    isPresenceActive
  } = useUserPresence();

  //Debug Ghost Users
  console.log('useUserPresence state:', {
    ghostUsersCount: ghostUsers.length,
    isPresenceActive,
    totalGhostUsers,
    currentForestId: currentForest?.id,
    currentUserId: user?.uid
  });

  const {
  todaysExercise,
  exerciseButtonState,
  isModalOpen,
  handleExerciseClick,
  handleModalClose,
  handleExerciseComplete,
  exerciseButtonTooltip
} = useDailyExercises();

  //Viewport position state
  const [viewportX, setViewportX] = useState(32);
  const [viewportY, setViewportY] = useState(18);

  //Avatar state
  const [playerDirection, setPlayerDirection] = useState<'up' | 'down' | 'left' | 'right'>('down');
  const [isPlayerMoving, setIsPlayerMoving] = useState(false);
  const [isMoving, setIsMoving] = useState(false);

  //Action menu state
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [actionMenuPosition, setActionMenuPosition] = useState({ x: 0, y: 0 });

  //Other state
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  //Get animal data
  const { animals } = useSelector((state: RootState) => state.game);
  const userAnimal = animals.find(animal => animal.id === user?.animalId);

  //Debug logging
  useEffect(() => {
    console.log('Entering the forest...');
    console.log('User presence active:', isPresenceActive);
    console.log('Total ghost users:', totalGhostUsers);
  }, [isPresenceActive, totalGhostUsers]);


  useEffect(() => {
    console.log('Player direction changed to:', playerDirection);
  }, [playerDirection]);

  useEffect(() => {
    console.log('Container size updated:', containerSize);
  }, [containerSize]);

  //Debug trees, ugh
  useEffect(() => {
    console.log('Trees updated:', {
      count: trees.length,
      stages: trees.map(t => ({ id: t.id, stage: t.maturityStage, pos: t.position }))
    });
  }, [trees]);

  //Center viewport on user's position on load
  useEffect(() => {
    if (user && containerSize.width > 0 && containerSize.height > 0) {
      const viewportTilesWide = Math.floor(containerSize.width / GAME_CONFIG.TILE_SIZE);
      const viewportTilesHigh = Math.floor(containerSize.height / GAME_CONFIG.TILE_SIZE);
      
      if (viewportTilesWide > 0 && viewportTilesHigh > 0) {
        const centerOffsetX = Math.floor(viewportTilesWide / 2);
        const centerOffsetY = Math.floor(viewportTilesHigh / 2);

        const idealViewportX = user.lastPosition.x - centerOffsetX;
        const idealViewportY = user.lastPosition.y - centerOffsetY;

        console.log('Initial viewport centering:', {
          playerPos: user.lastPosition,
          viewportSize: { width: viewportTilesWide, height: viewportTilesHigh },
          centerOffset: { x: centerOffsetX, y: centerOffsetY },
          idealViewport: { x: idealViewportX, y: idealViewportY }
        });
        
        const clampedViewportX = Math.max(0, Math.min(GAME_CONFIG.GRID_WIDTH - viewportTilesWide, idealViewportX));
        const clampedViewportY = Math.max(0, Math.min(GAME_CONFIG.GRID_HEIGHT - viewportTilesHigh, idealViewportY));
        
        console.log('Setting initial viewport:', {
          from: { x: viewportX, y: viewportY },
          to: { x: clampedViewportX, y: clampedViewportY }
        });
        
        setViewportX(clampedViewportX);
        setViewportY(clampedViewportY);
      }
    }
  }, [user, containerSize]);

  //Movement handler
  const handleMovement = useCallback(async (direction: 'up' | 'down' | 'left' | 'right') => {
    recordUserActivity('movement');
    console.log('=== MOVEMENT START ===');
    console.log('User state at start:', {
      userPosition: user?.lastPosition,
      currentViewport: { x: viewportX, y: viewportY }
    });
    
    if (!user || isMoving) return;
    
    const currentX = user.lastPosition.x;
    const currentY = user.lastPosition.y;
    
    console.log('Using position for calculation:', { currentX, currentY });
    
    //Calculate new position
    let newX = currentX;
    let newY = currentY;
    
    switch (direction) {
      case 'up': newY = currentY - 1; break;
      case 'down': newY = currentY + 1; break;
      case 'left': newX = currentX - 1; break;
      case 'right': newX = currentX + 1; break;
    }
    
    //Boundary and collision checking
    if (newX < 0 || newX >= GAME_CONFIG.GRID_WIDTH || 
        newY < 0 || newY >= GAME_CONFIG.GRID_HEIGHT) {
      console.log(`Cannot move: Out of bounds (${newX}, ${newY})`);
      setPlayerDirection(direction);
      return;
    }

    //Tree-aware movement
    const movementResult = calculateMovementWithTrees(currentX, currentY, direction, trees);

    if (!movementResult.canMove) {
      console.log(`Cannot move: ${movementResult.reason}`);
      setPlayerDirection(direction);
      return;
    }

    console.log(`Moving to: (${newX}, ${newY})`);
    
    //Valid movement
    setIsMoving(true);
    setPlayerDirection(direction);
    dispatch(updateUserAction({ lastPosition: { x: newX, y: newY } }));
    handlePositionChange(newX, newY);

    console.log('Redux updated, about to calculate viewport...');
    console.log('New position sent to Redux:', { x: newX, y: newY });

    //Viewport following, oh no
    if (containerSize.width > 0 && containerSize.height > 0) {
      const viewportTilesWide = Math.floor(containerSize.width / GAME_CONFIG.TILE_SIZE);
      const viewportTilesHigh = Math.floor(containerSize.height / GAME_CONFIG.TILE_SIZE);
      
      if (viewportTilesWide > 0 && viewportTilesHigh > 0) {
        const centerOffsetX = Math.floor(viewportTilesWide / 2);
        const centerOffsetY = Math.floor(viewportTilesHigh / 2);

        const idealViewportX = newX - centerOffsetX;
        const idealViewportY = newY - centerOffsetY;

        console.log('Viewport calculation details:', {
          player: { x: newX, y: newY },
          viewportSize: { width: viewportTilesWide, height: viewportTilesHigh },
          centerOffset: { x: centerOffsetX, y: centerOffsetY },
          idealViewport: { x: idealViewportX, y: idealViewportY }
        });
        
        const clampedViewportX = Math.max(0, Math.min(GAME_CONFIG.GRID_WIDTH - viewportTilesWide, idealViewportX));
        const clampedViewportY = Math.max(0, Math.min(GAME_CONFIG.GRID_HEIGHT - viewportTilesHigh, idealViewportY));
        
        console.log('Viewport update:', {
          player: { x: newX, y: newY },
          viewport: { x: clampedViewportX, y: clampedViewportY }
        });
        
        setViewportX(clampedViewportX);
        setViewportY(clampedViewportY);
      }
    }
    
    //db update
    updateUser(user.uid, { 
      lastPosition: { x: newX, y: newY },
      updatedAt: new Date()
    }).catch(console.error);

    setTimeout(() => setIsMoving(false), 200);
    console.log('=== MOVEMENT END ===');
    
  }, [user, isMoving, dispatch, containerSize, trees]);

  //Action handlers
  const handleAction = useCallback(async (action: GameAction) => {
  console.log('=== ACTION EXECUTION START ===');
  console.log('Action details:', action);
  
  try {
    switch (action.type) {
      case 'plant_seed':
        console.log('Executing plant seed action...');
        await handlePlantTree(
          action.position.x, 
          action.position.y, 
          user?.preferredColor || DEFAULT_COLOR_KEY
        );
        break;
          
        case 'water_tree':
          console.log('Executing water tree action...');
          await handleWaterTree(action.treeId);
          break;
          
        case 'exit_menu':
          console.log('Executing exit menu action...');
          setShowActionMenu(false);
          break;
      }
    } catch (error) {
      console.error('Action failed:', error);
      
      //Show user-friendly error messages
      if (error instanceof Error) {
        const message = getPlantingErrorMessage(error);
        //ToDo:  Put this into a modal, eventually
        console.log('User message:', message);
        alert(message); //ToDo: Modal. Someday.
      }
    }
    
    setShowActionMenu(false);
    console.log('=== ACTION EXECUTION END ===');
 }, [handlePlantTree, handleWaterTree, user]);


  //Keyboard controls
  useKeyboard({
    onArrowKey: handleMovement,
    onSpacebar: () => {
      if (!user) return;
      
      const playerX = user.lastPosition.x || 0;
      const playerY = user.lastPosition.y || 0;
      
      if (showActionMenu) {
        setShowActionMenu(false);
      } else {
        setActionMenuPosition({ x: playerX, y: playerY });
        setShowActionMenu(true);
      }
    },
    onEscape: () => {
      if (showActionMenu) {
        setShowActionMenu(false);
      } else {
        window.location.hash = '';
      }
    },
    enabled: !showActionMenu
  });

  //Keyboard state debug logs
  useEffect(() => {
    console.log('Keyboard controls enabled:', !showActionMenu, 'Menu open:', showActionMenu);
  }, [showActionMenu]);

  //Default container size
  useEffect(() => {
    setContainerSize({ width: 1024, height: 576 });
  }, []);

  //Debug logs
  if (user) {
    console.log('Passing to GameGrid:', {
      playerDirection,
      playerX: user.lastPosition.x,
      playerY: user.lastPosition.y,
      treesCount: trees.length
    });
  }

  //Loading state
  if (isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-green-900">
        <div className="text-white text-xl">Loading forest...</div>
      </div>
    );
  }

  //Error state
  if (error) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-red-900">
        <div className="text-white text-xl">Forest Error: {error}</div>
      </div>
    );
  }

  //No forest state
  if (!currentForest) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-gray-900">
        <div className="text-white text-xl">No forest found for your class code</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const backgroundStyle = {
    backgroundImage: `url(${ASSET_PATHS.BACKGROUNDS.FOREST})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: GAME_CONFIG.ANIMATIONS.SCENE_TRANSITION / 1000 }}
      className="h-screen w-screen overflow-hidden"
      style={backgroundStyle}
    >
      {/* Status Bar */}
      <div 
        className="w-full bg-black/20 backdrop-blur-sm border-b border-white/20"
        style={{ height: `${GAME_CONFIG.STATUS_BAR_HEIGHT_PERCENT}%` }}
      >
        <StatusBar 
          onExerciseClick={handleExerciseClick}
          exerciseButtonState={exerciseButtonState}
          exerciseButtonTooltip={exerciseButtonTooltip}
        />
      </div>

      {/* Game Area */}
      <div 
        className="w-full relative"
        style={{ height: `${100 - GAME_CONFIG.STATUS_BAR_HEIGHT_PERCENT}%` }}
      >
        {/* Forest Grid Container */}
        <div 
          className="absolute bg-green-100/10 border-2 border-white/20 rounded-lg"
          style={{
            width: `${GAME_CONFIG.FOREST_WIDTH_PERCENT}%`,
            height: `${GAME_CONFIG.FOREST_HEIGHT_PERCENT}%`,
            left: `${GAME_CONFIG.BORDER_PERCENT}%`,
            top: `${GAME_CONFIG.BORDER_PERCENT}%`,
          }}
        >
          {/* Active Game Area */}
          <div 
            className="absolute bg-green-200/20 border border-white/10 rounded"
            style={{
              width: `${GAME_CONFIG.ACTIVE_GAME_AREA_PERCENT}%`,
              height: `${GAME_CONFIG.ACTIVE_GAME_AREA_PERCENT}%`,
              left: `${(100 - GAME_CONFIG.ACTIVE_GAME_AREA_PERCENT) / 2}%`,
              top: `${(100 - GAME_CONFIG.ACTIVE_GAME_AREA_PERCENT) / 2}%`,
            }}
          >
            <GameGrid 
              viewportX={viewportX}
              viewportY={viewportY}
              playerX={user.lastPosition.x}
              playerY={user.lastPosition.y}
              playerDirection={playerDirection}
              playerAnimal={userAnimal || animals[0]}
              isPlayerMoving={isMoving}
              trees={trees}
              ghostUsers={ghostUsers}
              userPreferredColor={user?.preferredColor}
              onContainerSizeChange={setContainerSize}
            />

            {/* Action Menu */}
            {showActionMenu && user && (
              <ActionMenu
                x={user.lastPosition.x - viewportX} //position in viewport
                y={user.lastPosition.y - viewportY} //position in viewport
                actions={getAvailableActions(
                  actionMenuPosition.x, 
                  actionMenuPosition.y, 
                  playerDirection,
                  user, 
                  currentForest,
                  trees
                )}
                onAction={handleAction}
                onClose={() => setShowActionMenu(false)}
                isVisible={showActionMenu}
                trees={trees}
                userDirection={playerDirection}
              />
            )}
          </div>
        </div>

        {/* Debug, delete eventually probably*/}
        <div className="absolute bottom-4 left-4 text-white/60 text-xs bg-black/40 p-2 rounded">
          <div>Player: {user.username} ({user.initials})</div>
          <div>Position: ({user.lastPosition.x}, {user.lastPosition.y})</div>
          <div>Direction: {playerDirection}</div>
          <div>Forest: {currentForest?.name || 'None'}</div>
          <div>Trees: {trees.length} planted</div>
          <div>Ghost Users: {totalGhostUsers}</div>
          <div>Presence: {isPresenceActive ? 'Active' : 'Inactive'}</div>
          <div>Seeds: {user.seeds} | Water: {user.water}</div>
          <div className={showActionMenu ? 'text-red-300 font-bold' : ''}>
            Menu Open: {showActionMenu ? 'YES' : 'NO'}
          </div>
          <div>Menu Pos: ({actionMenuPosition.x}, {actionMenuPosition.y})</div>
          <div>Keyboard Enabled: {!showActionMenu ? 'YES' : 'NO'}</div>
          <div>Tree Bubbles: {showTreeBubbles ? 'ON' : 'OFF'}</div>
          <div>User Bubbles: {showUserBubbles ? 'ON' : 'OFF'}</div>
        </div>
      </div>

      <DailyExerciseModal
        isOpen={isModalOpen}
        exercise={todaysExercise}
        dayNumber={exerciseButtonState.currentDay}
        onClose={handleModalClose}
        onComplete={handleExerciseComplete}
      />

    </motion.div>
  );
};

export default ForestView;